import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  Platform,
  ActivityIndicator,
  useWindowDimensions,
} from 'react-native';
import { StatusBar } from 'expo-status-bar';
import { useTheme } from '../context/ThemeContext';
import HamburgerMenu from '../components/HamburgerMenu';
import ProfileIcon from '../components/ProfileIcon';
import Stopwatch from '../components/Stopwatch';
import { getApiUrl } from '../utils/apiConfig';
import {
  fetchExerciseRoutines,
  shareExerciseRoutine,
} from '../utils/exerciseRoutinesApi';
import { isFirstDegreeOrHigher } from '../utils/gradeAccess';
import {
  dedupeCoursesForDropdown,
  collapseCourseDisplayName,
  alignStudentCoursesToCatalog,
} from '../utils/coursesCatalog';

const ARROW_GOLD = '#FDFD0A';

function courseNameKey(name) {
  return collapseCourseDisplayName(name).toLowerCase();
}

export default function AssignmentsBuilderPage({ navigation, route }) {
  const theme = useTheme();
  const stopwatchRef = useRef(null);
  const { width } = useWindowDimensions();
  const isNarrow = width < 720;

  const [currentUser, setCurrentUser] = useState(route?.params?.user || null);
  const [students, setStudents] = useState([]);
  const [catalogNames, setCatalogNames] = useState([]);
  const [selectedStudentId, setSelectedStudentId] = useState(null);
  const [showStudentDropdown, setShowStudentDropdown] = useState(false);
  const [availableCourses, setAvailableCourses] = useState([]);
  const [selectedCourses, setSelectedCourses] = useState([]);
  const [availablePick, setAvailablePick] = useState(new Set());
  const [selectedPick, setSelectedPick] = useState(new Set());
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [loadingStudentCourses, setLoadingStudentCourses] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveToast, setSaveToast] = useState(null);
  const [sourceRoutines, setSourceRoutines] = useState([]);
  const [loadingSourceRoutines, setLoadingSourceRoutines] = useState(false);
  const [selectedRoutineId, setSelectedRoutineId] = useState(null);
  const [showRoutineDropdown, setShowRoutineDropdown] = useState(false);
  const [shareTargetStudentId, setShareTargetStudentId] = useState(null);
  const [showShareTargetDropdown, setShowShareTargetDropdown] = useState(false);
  const [sharingRoutine, setSharingRoutine] = useState(false);

  const canShareRoutines = isFirstDegreeOrHigher(currentUser);

  const catalogSorted = useMemo(
    () =>
      [...catalogNames].sort((a, b) =>
        a.localeCompare(b, undefined, { sensitivity: 'base' })
      ),
    [catalogNames]
  );

  const splitLists = useCallback(
    (assigned) => {
      const assignedKeys = new Set(assigned.map(courseNameKey));
      const selected = catalogSorted.filter((n) => assignedKeys.has(courseNameKey(n)));
      const available = catalogSorted.filter((n) => !assignedKeys.has(courseNameKey(n)));
      setSelectedCourses(selected);
      setAvailableCourses(available);
      setAvailablePick(new Set());
      setSelectedPick(new Set());
    },
    [catalogSorted]
  );

  useEffect(() => {
    const userId = route?.params?.userId || 1;
    fetch(`${getApiUrl()}/api/users/${userId}`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => data && setCurrentUser(data))
      .catch(() => {});
    void loadStudents();
    void loadCatalog();
  }, [route?.params?.userId]);

  const loadStudents = async () => {
    setLoadingStudents(true);
    try {
      const requesterId = route?.params?.userId || currentUser?.id;
      const requesterQ =
        requesterId != null
          ? `&requester_id=${encodeURIComponent(String(requesterId))}`
          : '';
      const res = await fetch(`${getApiUrl()}/api/users?visible=true${requesterQ}`);
      if (res.ok) {
        const data = await res.json();
        setStudents(Array.isArray(data) ? data : []);
      }
    } catch (e) {
      console.error('Error fetching students:', e);
      setStudents([]);
    } finally {
      setLoadingStudents(false);
    }
  };

  const loadCatalog = async () => {
    try {
      const res = await fetch(`${getApiUrl()}/api/courses`);
      if (res.ok) {
        const data = await res.json();
        const list = dedupeCoursesForDropdown(Array.isArray(data) ? data : []);
        setCatalogNames(
          list
            .map((c) => collapseCourseDisplayName(c.course_name))
            .filter(Boolean)
        );
      }
    } catch (e) {
      console.error('Error fetching courses:', e);
      setCatalogNames([]);
    }
  };

  const loadStudentCourses = async (studentId) => {
    setLoadingStudentCourses(true);
    setSaveToast(null);
    try {
      const res = await fetch(`${getApiUrl()}/api/users/${studentId}`);
      if (!res.ok) throw new Error('Could not load student');
      const student = await res.json();
      const aligned = alignStudentCoursesToCatalog(student.courses, catalogSorted);
      splitLists(aligned);
    } catch (e) {
      console.error(e);
      splitLists([]);
    } finally {
      setLoadingStudentCourses(false);
    }
  };

  const loadSourceRoutines = async (studentId) => {
    if (!studentId) {
      setSourceRoutines([]);
      setSelectedRoutineId(null);
      return;
    }
    setLoadingSourceRoutines(true);
    try {
      const rows = await fetchExerciseRoutines(studentId);
      setSourceRoutines(Array.isArray(rows) ? rows : []);
      setSelectedRoutineId(null);
      setShareTargetStudentId(null);
    } catch (e) {
      console.error(e);
      setSourceRoutines([]);
    } finally {
      setLoadingSourceRoutines(false);
    }
  };

  useEffect(() => {
    if (!selectedStudentId || catalogSorted.length === 0) return;
    void loadStudentCourses(selectedStudentId);
    if (canShareRoutines) {
      void loadSourceRoutines(selectedStudentId);
    }
  }, [selectedStudentId, catalogSorted.length, canShareRoutines]);

  const getStudentLabel = () => {
    if (!selectedStudentId) return 'Select student…';
    const s = students.find((x) => x.id === selectedStudentId);
    return s?.student_name || s?.username || 'Selected student';
  };

  const getRoutineLabel = () => {
    if (!selectedRoutineId) return 'Select routine…';
    const r = sourceRoutines.find((x) => x.id === selectedRoutineId);
    return r?.name || 'Selected routine';
  };

  const getShareTargetLabel = () => {
    if (!shareTargetStudentId) return 'Select student to share with…';
    const s = students.find((x) => x.id === shareTargetStudentId);
    return s?.student_name || s?.username || 'Selected student';
  };

  const handleShareRoutine = async () => {
    if (!selectedStudentId || !selectedRoutineId || !shareTargetStudentId) {
      setSaveToast({ type: 'error', message: 'Select a routine and a student to share with.' });
      return;
    }
    if (Number(shareTargetStudentId) === Number(selectedStudentId)) {
      setSaveToast({ type: 'error', message: 'Cannot share a routine with the same student.' });
      return;
    }
    const sharerId = currentUser?.id || route?.params?.userId;
    if (!sharerId) {
      setSaveToast({ type: 'error', message: 'Could not determine current user.' });
      return;
    }
    setSharingRoutine(true);
    setSaveToast(null);
    try {
      const result = await shareExerciseRoutine({
        routineId: selectedRoutineId,
        sharedWithStudentId: shareTargetStudentId,
        sharedByStudentId: sharerId,
      });
      setSaveToast({
        type: 'success',
        message: `Shared “${result.routine_name || 'routine'}” with ${result.recipient_name || 'student'}.`,
      });
      setShareTargetStudentId(null);
    } catch (e) {
      setSaveToast({
        type: 'error',
        message: e.message || 'Failed to share routine.',
      });
    } finally {
      setSharingRoutine(false);
    }
  };

  const toggleAvailablePick = (key) => {
    setSelectedPick(new Set());
    setAvailablePick((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleSelectedPick = (key) => {
    setAvailablePick(new Set());
    setSelectedPick((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const moveToSelected = () => {
    if (availablePick.size === 0) return;
    const moving = availableCourses.filter((n) => availablePick.has(courseNameKey(n)));
    const newSelected = [...selectedCourses, ...moving].sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: 'base' })
    );
    const moveKeys = new Set(moving.map(courseNameKey));
    setSelectedCourses(newSelected);
    setAvailableCourses(availableCourses.filter((n) => !moveKeys.has(courseNameKey(n))));
    setAvailablePick(new Set());
  };

  const moveToAvailable = () => {
    if (selectedPick.size === 0) return;
    const moving = selectedCourses.filter((n) => selectedPick.has(courseNameKey(n)));
    const newAvailable = [...availableCourses, ...moving].sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: 'base' })
    );
    const moveKeys = new Set(moving.map(courseNameKey));
    setAvailableCourses(newAvailable);
    setSelectedCourses(selectedCourses.filter((n) => !moveKeys.has(courseNameKey(n))));
    setSelectedPick(new Set());
  };

  const handleSave = async () => {
    if (!selectedStudentId) {
      setSaveToast({ type: 'error', message: 'Select a student first.' });
      return;
    }
    setSaving(true);
    setSaveToast(null);
    try {
      const res = await fetch(
        `${getApiUrl()}/api/users/${encodeURIComponent(String(selectedStudentId))}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ courses: selectedCourses }),
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setSaveToast({
          type: 'error',
          message: data.error || 'Failed to save courses',
        });
        return;
      }
      const aligned = alignStudentCoursesToCatalog(data.courses, catalogSorted);
      splitLists(aligned);
      setSaveToast({
        type: 'success',
        message: 'Course list updated for this student.',
      });
    } catch (e) {
      setSaveToast({
        type: 'error',
        message: e.message || 'Failed to save',
      });
    } finally {
      setSaving(false);
    }
  };

  const renderCourseList = (items, picked, onTogglePick, emptyLabel) => (
    <ScrollView style={styles.listScroll} nestedScrollEnabled keyboardShouldPersistTaps="handled">
      {items.length === 0 ? (
        <Text style={styles.listEmpty}>{emptyLabel}</Text>
      ) : (
        items.map((name) => {
          const key = courseNameKey(name);
          const isPicked = picked.has(key);
          return (
            <TouchableOpacity
              key={key}
              style={[styles.listItem, isPicked && styles.listItemPicked]}
              onPress={() => onTogglePick(key)}
              activeOpacity={0.8}
            >
              <Text style={[styles.listItemText, isPicked && styles.listItemTextPicked]} numberOfLines={2}>
                {name}
              </Text>
            </TouchableOpacity>
          );
        })
      )}
    </ScrollView>
  );

  const styles = getStyles(theme, isNarrow);

  return (
    <View
      style={styles.container}
      data-screen="assignments-builder"
      data-theme={theme.isDarkMode ? 'dark' : 'light'}
    >
      <StatusBar style={theme.isDarkMode ? 'light' : 'dark'} />

      <View style={styles.header}>
        <View style={styles.hamburgerContainer}>
          <HamburgerMenu navigation={navigation} stopwatchRef={stopwatchRef} />
        </View>
        <View style={styles.breadcrumbContainer}>
          <Text style={styles.breadcrumb}>Assignments Builder</Text>
        </View>
        <View style={styles.profileIconContainer}>
          <ProfileIcon navigation={navigation} user={currentUser} />
        </View>
      </View>

      <ScrollView style={styles.content} contentContainerStyle={styles.contentContainer}>
        <View style={styles.titleSection}>
          <Text style={styles.title}>Assignments Builder</Text>
          <Text style={styles.sectionGold}>Course Assignments</Text>
        </View>

        {saveToast ? (
          <View
            style={[
              styles.feedbackToast,
              saveToast.type === 'error'
                ? styles.feedbackToastError
                : styles.feedbackToastSuccess,
            ]}
            nativeID="assignments-builder-save-toast"
            {...(Platform.OS === 'web'
              ? {
                  dataSet: {
                    assignmentsBuilderToast: 'true',
                    assignmentsBuilderToastType: saveToast.type,
                  },
                }
              : {})}
          >
            <Text
              style={styles.feedbackToastMessage}
              {...(Platform.OS === 'web'
                ? { dataSet: { assignmentsBuilderToastText: 'true' } }
                : {})}
            >
              {saveToast.message}
            </Text>
            <TouchableOpacity
              onPress={() => setSaveToast(null)}
              style={styles.feedbackToastClose}
              hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
              accessibilityRole="button"
              accessibilityLabel="Dismiss"
              nativeID="assignments-builder-save-toast-close"
              {...(Platform.OS === 'web'
                ? { dataSet: { assignmentsBuilderToastClose: 'true' } }
                : {})}
            >
              <Text style={styles.feedbackToastCloseText}>✕</Text>
            </TouchableOpacity>
          </View>
        ) : null}

        <View style={styles.form}>
          <View style={styles.fieldContainer}>
            <Text style={styles.label}>Select Student</Text>
            <TouchableOpacity
              style={styles.dropdownTrigger}
              nativeID="assignments-builder-student-trigger"
              onPress={() => setShowStudentDropdown((v) => !v)}
              disabled={loadingStudents}
              {...(Platform.OS === 'web' ? { dataSet: { dropdownTrigger: 'true' } } : {})}
            >
              {loadingStudents ? (
                <ActivityIndicator size="small" color={theme.colors.heading} />
              ) : (
                <>
                  <Text
                    style={[
                      styles.dropdownTriggerText,
                      !selectedStudentId && styles.dropdownPlaceholder,
                    ]}
                    numberOfLines={1}
                  >
                    {getStudentLabel()}
                  </Text>
                  <Text style={styles.dropdownArrow}>{showStudentDropdown ? '▲' : '▼'}</Text>
                </>
              )}
            </TouchableOpacity>
            {showStudentDropdown && students.length > 0 ? (
              <ScrollView
                style={styles.dropdownList}
                nativeID="assignments-builder-student-list"
                {...(Platform.OS === 'web' ? { dataSet: { dropdownSurface: 'true' } } : {})}
                nestedScrollEnabled
                keyboardShouldPersistTaps="handled"
              >
                {students.map((s) => (
                  <TouchableOpacity
                    key={s.id}
                    style={[
                      styles.dropdownListItem,
                      selectedStudentId === s.id && styles.dropdownListItemSelected,
                    ]}
                    onPress={() => {
                      setSelectedStudentId(s.id);
                      setShowStudentDropdown(false);
                    }}
                    {...(Platform.OS === 'web' ? { dataSet: { dropdownRow: 'true' } } : {})}
                  >
                    <Text style={styles.dropdownListItemText}>
                      {s.student_name || s.username}
                      {s.student_grade ? ` · ${s.student_grade}` : ''}
                    </Text>
                  </TouchableOpacity>
                ))}
              </ScrollView>
            ) : null}
          </View>

          {selectedStudentId ? (
            <View style={styles.dualListSection}>
              <Text style={styles.coursesHeading}>Courses</Text>
              {loadingStudentCourses ? (
                <ActivityIndicator
                  style={{ marginVertical: 24 }}
                  color={theme.colors.heading}
                />
              ) : (
                <View style={[styles.dualListRow, isNarrow && styles.dualListRowStacked]}>
                  <View style={styles.listColumn}>
                    <Text style={styles.listColumnTitle}>Available</Text>
                    <View style={styles.listBox}>
                      {renderCourseList(
                        availableCourses,
                        availablePick,
                        toggleAvailablePick,
                        'No courses available'
                      )}
                    </View>
                  </View>

                  <View style={styles.arrowColumn}>
                    <TouchableOpacity
                      style={[
                        styles.arrowButton,
                        availablePick.size === 0 && styles.arrowButtonDisabled,
                      ]}
                      onPress={moveToSelected}
                      disabled={availablePick.size === 0}
                      accessibilityLabel="Move to selected"
                    >
                      <Text style={styles.arrowIcon}>→</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={[
                        styles.arrowButton,
                        selectedPick.size === 0 && styles.arrowButtonDisabled,
                      ]}
                      onPress={moveToAvailable}
                      disabled={selectedPick.size === 0}
                      accessibilityLabel="Move to available"
                    >
                      <Text style={styles.arrowIcon}>←</Text>
                    </TouchableOpacity>
                  </View>

                  <View style={styles.listColumn}>
                    <Text style={styles.listColumnTitle}>Selected</Text>
                    <View style={styles.listBox}>
                      {renderCourseList(
                        selectedCourses,
                        selectedPick,
                        toggleSelectedPick,
                        'No courses selected'
                      )}
                    </View>
                  </View>
                </View>
              )}

              <TouchableOpacity
                style={[styles.saveButton, saving && styles.saveButtonDisabled]}
                onPress={() => void handleSave()}
                disabled={saving || loadingStudentCourses}
              >
                {saving ? (
                  <ActivityIndicator color={theme.isDarkMode ? '#000' : '#FFF'} />
                ) : (
                  <Text style={styles.saveButtonText}>Save</Text>
                )}
              </TouchableOpacity>

              {canShareRoutines ? (
                <View style={styles.routineShareSection}>
                  <Text style={styles.routineShareHeading}>Routine Sharing</Text>
                  <Text style={styles.routineShareHelp}>
                    Share a routine from this student to another student (1st degree and above).
                  </Text>

                  <View style={styles.fieldContainer}>
                    <Text style={styles.label}>Routine to share</Text>
                    <TouchableOpacity
                      style={styles.dropdownTrigger}
                      onPress={() => setShowRoutineDropdown((v) => !v)}
                      disabled={loadingSourceRoutines}
                      {...(Platform.OS === 'web' ? { dataSet: { dropdownTrigger: 'true' } } : {})}
                    >
                      {loadingSourceRoutines ? (
                        <ActivityIndicator size="small" color={theme.colors.heading} />
                      ) : (
                        <>
                          <Text
                            style={[
                              styles.dropdownTriggerText,
                              !selectedRoutineId && styles.dropdownPlaceholder,
                            ]}
                            numberOfLines={1}
                          >
                            {getRoutineLabel()}
                          </Text>
                          <Text style={styles.dropdownArrow}>
                            {showRoutineDropdown ? '▲' : '▼'}
                          </Text>
                        </>
                      )}
                    </TouchableOpacity>
                    {showRoutineDropdown && sourceRoutines.length > 0 ? (
                      <ScrollView
                        style={styles.dropdownList}
                        nestedScrollEnabled
                        keyboardShouldPersistTaps="handled"
                        {...(Platform.OS === 'web' ? { dataSet: { dropdownSurface: 'true' } } : {})}
                      >
                        {sourceRoutines.map((routine) => (
                          <TouchableOpacity
                            key={routine.id}
                            style={[
                              styles.dropdownListItem,
                              selectedRoutineId === routine.id && styles.dropdownListItemSelected,
                            ]}
                            onPress={() => {
                              setSelectedRoutineId(routine.id);
                              setShowRoutineDropdown(false);
                            }}
                            {...(Platform.OS === 'web' ? { dataSet: { dropdownRow: 'true' } } : {})}
                          >
                            <Text style={styles.dropdownListItemText}>{routine.name}</Text>
                          </TouchableOpacity>
                        ))}
                      </ScrollView>
                    ) : null}
                    {!loadingSourceRoutines && sourceRoutines.length === 0 ? (
                      <Text style={styles.routineShareHelp}>This student has no routines yet.</Text>
                    ) : null}
                  </View>

                  <View style={styles.fieldContainer}>
                    <Text style={styles.label}>Share with student</Text>
                    <TouchableOpacity
                      style={styles.dropdownTrigger}
                      onPress={() => setShowShareTargetDropdown((v) => !v)}
                      {...(Platform.OS === 'web' ? { dataSet: { dropdownTrigger: 'true' } } : {})}
                    >
                      <Text
                        style={[
                          styles.dropdownTriggerText,
                          !shareTargetStudentId && styles.dropdownPlaceholder,
                        ]}
                        numberOfLines={1}
                      >
                        {getShareTargetLabel()}
                      </Text>
                      <Text style={styles.dropdownArrow}>
                        {showShareTargetDropdown ? '▲' : '▼'}
                      </Text>
                    </TouchableOpacity>
                    {showShareTargetDropdown ? (
                      <ScrollView
                        style={styles.dropdownList}
                        nestedScrollEnabled
                        keyboardShouldPersistTaps="handled"
                        {...(Platform.OS === 'web' ? { dataSet: { dropdownSurface: 'true' } } : {})}
                      >
                        {students
                          .filter((s) => Number(s.id) !== Number(selectedStudentId))
                          .map((s) => (
                            <TouchableOpacity
                              key={`share-target-${s.id}`}
                              style={[
                                styles.dropdownListItem,
                                shareTargetStudentId === s.id && styles.dropdownListItemSelected,
                              ]}
                              onPress={() => {
                                setShareTargetStudentId(s.id);
                                setShowShareTargetDropdown(false);
                              }}
                              {...(Platform.OS === 'web' ? { dataSet: { dropdownRow: 'true' } } : {})}
                            >
                              <Text style={styles.dropdownListItemText}>
                                {s.student_name || s.username}
                                {s.student_grade ? ` · ${s.student_grade}` : ''}
                              </Text>
                            </TouchableOpacity>
                          ))}
                      </ScrollView>
                    ) : null}
                  </View>

                  <TouchableOpacity
                    style={[
                      styles.saveButton,
                      (sharingRoutine || !selectedRoutineId || !shareTargetStudentId) &&
                        styles.saveButtonDisabled,
                    ]}
                    onPress={() => void handleShareRoutine()}
                    disabled={sharingRoutine || !selectedRoutineId || !shareTargetStudentId}
                  >
                    {sharingRoutine ? (
                      <ActivityIndicator color={theme.isDarkMode ? '#000' : '#FFF'} />
                    ) : (
                      <Text style={styles.saveButtonText}>Share Routine</Text>
                    )}
                  </TouchableOpacity>
                </View>
              ) : null}
            </View>
          ) : null}
        </View>
      </ScrollView>

      <Stopwatch ref={stopwatchRef} />
    </View>
  );
}

const getStyles = (theme, isNarrow) =>
  StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: theme.isDarkMode ? theme.colors.background : '#FFFFFF',
    },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingTop: 50,
      paddingBottom: 20,
      paddingHorizontal: 20,
    },
    hamburgerContainer: { marginRight: 15 },
    breadcrumbContainer: { flex: 1 },
    breadcrumb: {
      fontSize: 18,
      fontWeight: '600',
      color: theme.colors.heading,
    },
    profileIconContainer: {},
    content: { flex: 1 },
    contentContainer: { padding: 20, paddingBottom: 40 },
    titleSection: { marginBottom: 24, alignItems: 'center' },
    title: {
      fontSize: 32,
      fontWeight: 'bold',
      color: theme.isDarkMode ? '#FDFD0A' : '#000000',
      marginBottom: 8,
      textAlign: 'center',
      textTransform: 'uppercase',
      letterSpacing: 1,
    },
    sectionGold: {
      fontSize: 20,
      fontWeight: '700',
      color: theme.colors.heading,
      textAlign: 'center',
    },
    form: { width: '100%', maxWidth: 900, alignSelf: 'center' },
    fieldContainer: { marginBottom: 20, zIndex: 2 },
    label: {
      fontSize: 16,
      fontWeight: '600',
      color: theme.colors.text,
      marginBottom: 8,
    },
    dropdownTrigger: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      backgroundColor: theme.isDarkMode ? theme.colors.inputBackground : '#F5F5F5',
      borderRadius: 8,
      padding: 12,
      borderWidth: 1,
      borderColor: theme.colors.border,
      minHeight: 48,
    },
    dropdownTriggerText: {
      flex: 1,
      fontSize: 16,
      color: theme.colors.text,
      marginRight: 8,
      backgroundColor: 'transparent',
    },
    dropdownPlaceholder: { opacity: 0.55, backgroundColor: 'transparent' },
    dropdownArrow: { fontSize: 14, color: theme.colors.heading, backgroundColor: 'transparent' },
    dropdownList: {
      marginTop: 8,
      backgroundColor: theme.isDarkMode ? theme.colors.surface : '#F5F5F5',
      borderRadius: 8,
      borderWidth: 1,
      borderColor: theme.colors.border,
      maxHeight: 220,
    },
    dropdownListItem: {
      padding: 12,
      borderBottomWidth: 1,
      borderBottomColor: theme.colors.border,
      backgroundColor: 'transparent',
    },
    dropdownListItemSelected: {
      backgroundColor: theme.isDarkMode
        ? 'rgba(253, 253, 10, 0.12)'
        : 'rgba(212, 175, 55, 0.15)',
    },
    dropdownListItemText: { fontSize: 16, color: theme.colors.text, backgroundColor: 'transparent' },
    dualListSection: { marginTop: 8 },
    coursesHeading: {
      fontSize: 18,
      fontWeight: '700',
      color: theme.colors.heading,
      marginBottom: 12,
      textAlign: 'center',
      backgroundColor: 'transparent',
    },
    dualListRow: {
      flexDirection: 'row',
      alignItems: 'stretch',
      gap: 12,
    },
    dualListRowStacked: {
      flexDirection: 'column',
    },
    listColumn: { flex: 1, minWidth: isNarrow ? undefined : 140 },
    listColumnTitle: {
      fontSize: 15,
      fontWeight: '600',
      color: theme.colors.text,
      marginBottom: 8,
      textAlign: 'center',
    },
    listBox: {
      minHeight: 220,
      maxHeight: 320,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: theme.colors.border,
      backgroundColor: theme.isDarkMode ? theme.colors.surface : '#F5F5F5',
      overflow: 'hidden',
    },
    listScroll: { flex: 1 },
    listItem: {
      paddingVertical: 10,
      paddingHorizontal: 12,
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: theme.colors.border,
    },
    listItemPicked: {
      backgroundColor: theme.isDarkMode
        ? 'rgba(253, 253, 10, 0.2)'
        : 'rgba(212, 175, 55, 0.35)',
    },
    listItemText: { fontSize: 15, color: theme.colors.text },
    listItemTextPicked: { fontWeight: '700', color: theme.isDarkMode ? '#FDFD0A' : '#000' },
    listEmpty: {
      padding: 16,
      fontSize: 14,
      color: theme.colors.text,
      opacity: 0.65,
      fontStyle: 'italic',
      textAlign: 'center',
    },
    arrowColumn: {
      justifyContent: 'center',
      alignItems: 'center',
      gap: 16,
      paddingVertical: isNarrow ? 8 : 0,
      flexDirection: isNarrow ? 'row' : 'column',
    },
    arrowButton: {
      width: 48,
      height: 48,
      borderRadius: 8,
      borderWidth: 2,
      borderColor: ARROW_GOLD,
      backgroundColor: theme.isDarkMode ? '#1a1a1a' : '#fff',
      justifyContent: 'center',
      alignItems: 'center',
    },
    arrowButtonDisabled: { opacity: 0.35 },
    arrowIcon: {
      fontSize: 28,
      fontWeight: '700',
      color: ARROW_GOLD,
    },
    saveButton: {
      marginTop: 24,
      backgroundColor: theme.colors.primary || '#d4af37',
      paddingVertical: 15,
      borderRadius: 8,
      alignItems: 'center',
    },
    saveButtonDisabled: { opacity: 0.7 },
    saveButtonText: {
      fontSize: 16,
      fontWeight: '700',
      color: theme.isDarkMode ? '#000000' : '#FFFFFF',
    },
    feedbackToast: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      paddingVertical: 12,
      paddingHorizontal: 12,
      borderRadius: 8,
      marginBottom: 20,
      width: '100%',
      maxWidth: 600,
      alignSelf: 'center',
    },
    feedbackToastError: {
      backgroundColor: '#c62828',
    },
    feedbackToastSuccess: {
      backgroundColor: '#2e7d32',
    },
    feedbackToastMessage: {
      flex: 1,
      color: '#ffffff',
      fontSize: 15,
      lineHeight: 20,
      marginRight: 8,
      paddingRight: 4,
      fontWeight: '700',
      backgroundColor: 'transparent',
    },
    feedbackToastClose: {
      padding: 4,
      marginTop: -2,
      backgroundColor: 'transparent',
    },
    feedbackToastCloseText: {
      color: '#ffffff',
      fontSize: 18,
      fontWeight: '700',
      backgroundColor: 'transparent',
    },
    routineShareSection: {
      marginTop: 36,
      paddingTop: 24,
      borderTopWidth: 1,
      borderTopColor: theme.colors.border,
    },
    routineShareHeading: {
      fontSize: 18,
      fontWeight: '700',
      color: theme.colors.heading,
      marginBottom: 8,
      textAlign: 'center',
      backgroundColor: 'transparent',
    },
    routineShareHelp: {
      fontSize: 13,
      color: theme.colors.text,
      opacity: 0.75,
      marginBottom: 16,
      textAlign: 'center',
      fontStyle: 'italic',
      backgroundColor: 'transparent',
    },
  });
